import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const GOVX_WIDGET_MATCHER = /get verified|discount available for military|first responders|medical workers|teachers/i;

/** @type {Promise<string> | null} */
let govxWidgetMarkupPromise = null;
/** @type {Promise<{ scriptSrc: string, styleHref: string }> | null} */
let govxWidgetAssetsPromise = null;

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.#loadVerificationWidget();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    if (event?.detail?.data?.didError) return;

    // Do not auto-open when cart is only being edited inside the cart itself.
    if (event?.detail?.data?.source === 'cart-items-component') return;

    this.#loadVerificationWidget();
    this.showDialog();
    requestAnimationFrame(() => this.#loadVerificationWidget());
  };

  open() {
    this.#loadVerificationWidget();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  async #loadVerificationWidget() {
    const mount = this.querySelector('[data-cart-drawer-verification]');
    if (!(mount instanceof HTMLElement) || mount.dataset.loaded === 'true') return;

    try {
      if (!govxWidgetMarkupPromise) {
        govxWidgetMarkupPromise = this.#fetchVerificationWidgetMarkup();
      }

      const markup = await govxWidgetMarkupPromise;
      if (!markup) {
        govxWidgetMarkupPromise = null;
        return;
      }

      await this.#ensureVerificationDependencies();
      mount.innerHTML = markup;
      this.#executeInjectedScripts(mount);
      mount.hidden = false;
      mount.dataset.loaded = 'true';
    } catch (error) {
      console.error('Failed to load verification widget in cart drawer', error);
      govxWidgetMarkupPromise = null;
    }
  }

  async #fetchVerificationWidgetMarkup() {
    const response = await fetch(Theme.routes.cart_url, {
      headers: {
        Accept: 'text/html',
      },
    });

    if (!response.ok) return '';

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const appBlocks = Array.from(parsed.querySelectorAll('.shopify-app-block'));
    let widget = appBlocks.find((block) => {
      if (!(block instanceof HTMLElement)) return false;
      if (block.querySelector('[data-govx-id], .govx-id-v2-wrapper, .govx-id-v2-full')) return true;
      return GOVX_WIDGET_MATCHER.test(block.textContent || '');
    });

    if (!(widget instanceof HTMLElement)) {
      widget = parsed.querySelector('[id*="govx"], [class*="govx"], [data-block-id*="govx"]');
    }

    if (!widget) {
      const matchingNode = Array.from(parsed.querySelectorAll('button, a, div, p, span')).find((element) => {
        const text = (element.textContent || '').trim();
        return text.length > 0 && text.length < 260 && GOVX_WIDGET_MATCHER.test(text);
      });

      widget =
        matchingNode?.closest('[id^="shopify-block-"], [class*="shopify-block"], .section-content-wrapper > *') ||
        matchingNode ||
        null;
    }

    if (!(widget instanceof HTMLElement)) return '';

    return widget.outerHTML;
  }

  async #fetchVerificationAssets() {
    const response = await fetch(Theme.routes.cart_url, {
      headers: {
        Accept: 'text/html',
      },
    });

    if (!response.ok) return { scriptSrc: '', styleHref: '' };

    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const styleHref = parsed.querySelector('link[href*="govx-id.css"]')?.getAttribute('href') || '';
    const scriptSrc = parsed.querySelector('script[src*="govx-id.js"]')?.getAttribute('src') || '';

    return { scriptSrc, styleHref };
  }

  async #ensureVerificationDependencies() {
    if (!govxWidgetAssetsPromise) {
      govxWidgetAssetsPromise = this.#fetchVerificationAssets();
    }

    const { scriptSrc, styleHref } = await govxWidgetAssetsPromise;

    if (styleHref && !document.querySelector(`link[href="${styleHref}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = styleHref;
      link.type = 'text/css';
      link.media = 'all';
      document.head.appendChild(link);
    }

    if (scriptSrc && !document.querySelector(`script[src="${scriptSrc}"]`)) {
      await new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = scriptSrc;
        script.type = 'text/javascript';
        script.defer = true;
        script.onload = resolve;
        script.onerror = resolve;
        document.head.appendChild(script);
      });
    }
  }

  #executeInjectedScripts(root) {
    const scripts = root.querySelectorAll('script');

    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach((attribute) => {
        newScript.setAttribute(attribute.name, attribute.value);
      });

      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }

      oldScript.replaceWith(newScript);
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
